import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { MapPin } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip
} from 'recharts';

export default function VenueIntelligence() {
    const [venues, setVenues] = useState([]);
    const [selectedVenue, setSelectedVenue] = useState('');
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        API.get('/venues').then(res => setVenues(res.data.venues || []));
    }, []);

    useEffect(() => {
        if (selectedVenue) {
            setLoading(true);
            API.get(`/analytics/venue/${selectedVenue}`).then(res => {
                setAnalysis(res.data);
                setLoading(false);
            }).catch(() => setLoading(false));
        }
    }, [selectedVenue]);

    const radarData = analysis ? [
        { subject: 'Batting', value: (analysis.avg_first_innings_score / 200) * 100 },
        { subject: 'Chase', value: analysis.chase_success_rate },
        { subject: 'Pace', value: analysis.pace_advantage },
        { subject: 'Spin', value: analysis.spin_advantage },
        { subject: 'Boundaries', value: (analysis.boundary_frequency / 15) * 100 },
        { subject: 'Sixes', value: (analysis.six_frequency / 7) * 100 },
    ] : [];

    return (
        <div className="space-y-6" data-testid="venue-intelligence">
            <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                    <MapPin className="w-5 h-5 text-green-400" />
                    <h2 className="heading-primary text-base md:text-xl text-white">VENUE INTELLIGENCE ENGINE</h2>
                </div>
                <Select value={selectedVenue} onValueChange={setSelectedVenue}>
                    <SelectTrigger data-testid="venue-selector" className="bg-black/50 border-white/10 text-white h-12 max-w-lg">
                        <SelectValue placeholder="Select a venue..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10">
                        {venues.map(v => (
                            <SelectItem key={v.id} value={v.id} className="text-white hover:bg-white/10">
                                {v.name} - {v.city}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {!selectedVenue && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {venues.map(venue => (
                        <div key={venue.id} data-testid={`venue-card-${venue.id}`}
                            className="glass-card p-6 cursor-pointer hover:border-green-500/30 transition-all"
                            onClick={() => setSelectedVenue(venue.id)}>
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h3 className="heading-primary text-sm text-white">{venue.name}</h3>
                                    <p className="text-xs text-white/40 mt-1">{venue.city}</p>
                                </div>
                                <MapPin className="w-4 h-4 text-green-400" />
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-4">
                                <div>
                                    <div className="tech-mono text-[10px] text-white/30">AVG SCORE</div>
                                    <div className="heading-primary text-xl text-white">{venue.avg_first_innings}</div>
                                </div>
                                <div>
                                    <div className="tech-mono text-[10px] text-white/30">CHASE WIN %</div>
                                    <div className="heading-primary text-xl text-green-400">{(venue.chase_win_pct * 100).toFixed(0)}%</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {analysis && selectedVenue && !loading && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-6 glass-card p-6">
                        <h3 className="heading-primary text-sm text-white mb-4">VENUE PROFILE</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <RadarChart data={radarData}>
                                <PolarGrid stroke="#333" />
                                <PolarAngleAxis dataKey="subject" tick={{ fill: '#999', fontSize: 11 }} />
                                <PolarRadiusAxis hide />
                                <Radar dataKey="value" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.2} />
                                <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="lg:col-span-6 grid grid-cols-2 gap-4">
                        <div className="glass-card p-4">
                            <div className="tech-mono text-[10px] text-white/30">AVG 1ST INNINGS</div>
                            <div className="heading-primary text-3xl text-white">{analysis.avg_first_innings_score}</div>
                        </div>
                        <div className="glass-card p-4">
                            <div className="tech-mono text-[10px] text-white/30">AVG 2ND INNINGS</div>
                            <div className="heading-primary text-3xl text-white">{analysis.avg_second_innings_score}</div>
                        </div>
                        <div className="glass-card p-4">
                            <div className="tech-mono text-[10px] text-white/30">CHASE SUCCESS</div>
                            <div className="heading-primary text-3xl text-green-400">{analysis.chase_success_rate}%</div>
                        </div>
                        <div className="glass-card p-4">
                            <div className="tech-mono text-[10px] text-white/30">BAT FIRST ADV</div>
                            <div className="heading-primary text-3xl text-cyan-400">{analysis.batting_first_advantage}%</div>
                        </div>
                        <div className="glass-card p-4">
                            <div className="tech-mono text-[10px] text-white/30">PACE ADVANTAGE</div>
                            <div className="heading-primary text-3xl text-orange-400">{analysis.pace_advantage}%</div>
                        </div>
                        <div className="glass-card p-4">
                            <div className="tech-mono text-[10px] text-white/30">SPIN ADVANTAGE</div>
                            <div className="heading-primary text-3xl text-purple-400">{analysis.spin_advantage}%</div>
                        </div>
                        <div className="glass-card p-4">
                            <div className="tech-mono text-[10px] text-white/30">DEW FACTOR</div>
                            <div className="heading-primary text-3xl text-blue-400">{analysis.dew_factor}%</div>
                        </div>
                        <div className="glass-card p-4">
                            <div className="tech-mono text-[10px] text-white/30">TOSS CALL</div>
                            <div className="heading-primary text-xl text-yellow-400 uppercase">{analysis.recommended_action_on_toss}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
